/**
 * The recurrence editor in a task's detail expansion: preset segments plus a
 * Custom popover (interval + unit, weekday toggles for weekly). Weekday
 * selections force interval 1 per the shared normalization; the editor
 * enforces it rather than surprising the user at save time.
 */
import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Repeat } from "lucide-react";
import { describeRecurrence, parseRecurrence, type Recurrence } from "@hydra-notes/shared";
import s from "./RecurrenceEditor.module.css";

const WEEKDAYS = [
  { d: 1, label: "Mon" },
  { d: 2, label: "Tue" },
  { d: 3, label: "Wed" },
  { d: 4, label: "Thu" },
  { d: 5, label: "Fri" },
  { d: 6, label: "Sat" },
  { d: 0, label: "Sun" },
];

type Preset = "none" | "daily" | "weekly" | "monthly" | "custom";

function presetOf(rule: Recurrence | undefined): Preset {
  if (!rule) return "none";
  if (rule.weekdays?.length) return "weekly";
  if (rule.interval === 1) {
    return rule.unit === "day" ? "daily" : rule.unit === "week" ? "weekly" : "monthly";
  }
  return "custom";
}

export function RecurrenceEditor({
  rule,
  readOnly,
  onChange,
}: {
  rule: Recurrence | undefined;
  readOnly: boolean;
  onChange: (rule: Recurrence | undefined) => void;
}) {
  const preset = presetOf(rule);
  const [customOpen, setCustomOpen] = useState(false);

  const setPreset = (p: Preset) => {
    if (readOnly) return;
    if (p === "none") onChange(undefined);
    else if (p === "daily") onChange({ unit: "day", interval: 1 });
    else if (p === "weekly") onChange({ unit: "week", interval: 1 });
    else if (p === "monthly") onChange({ unit: "month", interval: 1 });
    else setCustomOpen(true);
  };

  const toggleWeekday = (d: number) => {
    if (readOnly) return;
    const current = new Set(rule?.weekdays ?? []);
    if (current.has(d)) current.delete(d);
    else current.add(d);
    const normalized = parseRecurrence({ unit: "week", interval: 1, weekdays: [...current] });
    // empty set falls back to plain weekly
    onChange(normalized ?? { unit: "week", interval: 1 });
  };

  return (
    <div className={s.wrap}>
      <span className={s.label}>
        <Repeat size={12} /> Repeats
      </span>
      <div className={s.segments} role="radiogroup" aria-label="Recurrence">
        {(["none", "daily", "weekly", "monthly", "custom"] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={preset === p}
            disabled={readOnly}
            className={[s.segment, preset === p ? s.segmentActive : ""].join(" ")}
            onClick={() => setPreset(p)}
          >
            {p === "none" ? "None" : p[0]!.toUpperCase() + p.slice(1)}
          </button>
        ))}
        <CustomPopover open={customOpen} onOpenChange={setCustomOpen} rule={rule} onChange={onChange} />
      </div>
      {preset === "weekly" ? (
        <div className={s.weekdays}>
          {WEEKDAYS.map(({ d, label }) => (
            <button
              key={d}
              type="button"
              disabled={readOnly}
              className={[s.weekday, rule?.weekdays?.includes(d) ? s.weekdayOn : ""].join(" ")}
              onClick={() => toggleWeekday(d)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {rule ? <span className={s.humanized}>{describeRecurrence(rule)}</span> : null}
    </div>
  );
}

function CustomPopover({
  open,
  onOpenChange,
  rule,
  onChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  rule: Recurrence | undefined;
  onChange: (rule: Recurrence | undefined) => void;
}) {
  const [interval, setInterval] = useState(rule?.interval ?? 2);
  const [unit, setUnit] = useState<Recurrence["unit"]>(rule?.unit ?? "week");

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Anchor />
      <Popover.Portal>
        <Popover.Content className={s.popover} sideOffset={6} align="start">
          <label className={s.popRow}>
            Every
            <input
              type="number"
              min={1}
              max={365}
              value={interval}
              className={s.popNumber}
              onChange={(e) => setInterval(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
            />
            <select className={s.popSelect} value={unit} onChange={(e) => setUnit(e.target.value as Recurrence["unit"])}>
              <option value="day">days</option>
              <option value="week">weeks</option>
              <option value="month">months</option>
            </select>
          </label>
          <button
            type="button"
            className={s.popApply}
            onClick={() => {
              const normalized = parseRecurrence({ unit, interval });
              if (normalized) onChange(normalized);
              onOpenChange(false);
            }}
          >
            Apply
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
